// The icon set — conventional line icons, drawn on Lucide's conventions.
//
// These were hand-drawn once, on an uneven ink line, to stay out of the
// geometric register the rest of the app avoids. That was the wrong place to
// spend the character budget: an icon in a nav bar is wayfinding, and a nest, a
// pressed-herbarium sheet and a toadstool do not read as Home, Library and You
// at 21px no matter how well they are drawn. The hand-made register lives where
// it costs nothing to recognise — the hand-cut radii (--radius-hand), the wavy
// .rule, the paper texture, Fraunces and Petrona.
//
// So: 24x24 field, stroke-only, round caps and joins, geometry on whole and half
// pixels, stroke-width 2. Every icon uses currentColor, so it inherits from
// whatever it sits in — across the theme swap and across the .correct/.wrong
// state backgrounds — with no per-variant rules.

import { el, ensureStyle } from '../dom';

export type IconName =
  | 'home'
  | 'book'
  | 'user'
  | 'chart'
  | 'flame'
  | 'music'
  | 'tag'
  | 'feather'
  | 'eye'
  | 'volume'
  | 'check'
  | 'cross'
  | 'arrow-left'
  | 'arrow-right'
  | 'swap'
  | 'compare'
  | 'compare-rows'
  | 'binoculars'
  | 'download'
  | 'file-down'
  | 'file-up'
  | 'log-out'
  | 'sun'
  | 'moon'
  | 'monitor'
  | 'waveform'
  | 'spectrogram'
  | 'alert'
  | 'set-aside'
  | 'undo'
  | 'sliders'
  | 'birdle'
  | 'calendar'
  | 'chevron-left'
  | 'chevron-right'
  | 'info';

/** Inner markup only — the <svg> wrapper, viewBox and stroke are set by icon(). */
const PATHS: Record<IconName, string> = {
  // Home tab.
  home:
    `<path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>` +
    `<path d="M9 22V12h6v10"/>`,

  // Library tab, and the "browse the species list" links.
  book:
    `<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>` +
    `<path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>`,

  // You tab.
  user:
    `<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>` +
    `<circle cx="12" cy="7" r="4"/>`,

  // Stats tab.
  chart:
    `<path d="M3 3v18h18"/>` +
    `<path d="M8 17v-4"/>` +
    `<path d="M13 17V8"/>` +
    `<path d="M18 17v-6"/>`,

  // Streak.
  flame:
    `<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.07-2.14-.22-4.05 2-6 ` +
    `.5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.15.43-2.29 1-3a2.5 ` +
    `2.5 0 0 0 2.5 2.5z"/>`,

  // Sound ID, and the recording count in the library.
  music:
    `<path d="M9 18V5l12-2v13"/>` +
    `<circle cx="6" cy="18" r="3"/>` +
    `<circle cx="18" cy="16" r="3"/>`,

  // Name Match: a label tied to a thing.
  tag:
    `<path d="M12 2H2v10l9.29 9.29a2.42 2.42 0 0 0 3.42 0l6.58-6.58a2.42 2.42 0 0 ` +
    `0 0-3.42Z"/>` +
    `<circle cx="7" cy="7" r="1.2"/>`,

  // The app's own mark, on the sign-in screen. The one place a bird-specific
  // glyph is doing real work rather than labelling a destination.
  feather:
    `<path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"/>` +
    `<path d="M16 8 2 22"/>` +
    `<path d="M17.5 15H9"/>`,

  // Photo ID.
  eye:
    `<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>` +
    `<circle cx="12" cy="12" r="3"/>`,

  // "Hear this one too" â€” the speaker on a wrong Sound ID pick.
  // One arc, not two. The second ring reached the corners of the 24-square, and
  // this glyph's only home is a 30px disc — the outer wave was clipped by the
  // disc's own edge on every render. Dropping it leaves the ink left of centre,
  // so the whole thing is nudged back into the middle.
  volume:
    `<g transform="translate(2.4 0)">` +
    `<path d="M11 5 6 9H2v6h4l5 4z"/>` +
    `<path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>` +
    `</g>`,

  check: `<path d="M20 6 9 17l-5-5"/>`,

  cross: `<path d="M18 6 6 18"/><path d="m6 6 12 12"/>`,

  'arrow-left': `<path d="M19 12H5"/><path d="m12 19-7-7 7-7"/>`,

  'arrow-right': `<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>`,

  // Two birds swapped for each other: the mix-up list and the pair drill.
  swap:
    `<path d="M4 8h13"/><path d="m14 5 3 3-3 3"/>` +
    `<path d="M20 16H7"/><path d="m10 13-3 3 3 3"/>`,

  // The side-by-side view: one frame split down the middle, two birds in it.
  compare:
    `<rect x="3" y="4" width="18" height="16" rx="2.5"/>` +
    `<path d="M12 4v16"/>`,

  // The same frame split the other way. The compare view's rotate control shows
  // the layout it will switch TO, so the two icons are a pair and the button is
  // legible without a word on it.
  'compare-rows':
    `<rect x="3" y="4" width="18" height="16" rx="2.5"/>` +
    `<path d="M3 12h18"/>`,

  // Field Test: the one round that draws on all three skills, and the only
  // thing you carry that is for looking AND listening.
  binoculars:
    `<circle cx="6" cy="15" r="4"/>` +
    `<circle cx="18" cy="15" r="4"/>` +
    `<path d="M10 15h4"/>` +
    `<path d="M6 11V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v6"/>` +
    `<path d="M14 11V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v6"/>`,

  // Media pack download.
  download:
    `<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>` +
    `<path d="m7 10 5 5 5-5"/>` +
    `<path d="M12 15V3"/>`,

  // Back up: you come away with a file.
  'file-down':
    `<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/>` +
    `<path d="M14 2v4a2 2 0 0 0 2 2h4"/>` +
    `<path d="M12 12v6"/>` +
    `<path d="m9 15 3 3 3-3"/>`,

  // Restore: you hand a file back.
  'file-up':
    `<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/>` +
    `<path d="M14 2v4a2 2 0 0 0 2 2h4"/>` +
    `<path d="M12 18v-6"/>` +
    `<path d="m9 15 3-3 3 3"/>`,

  'log-out':
    `<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>` +
    `<path d="m16 17 5-5-5-5"/>` +
    `<path d="M21 12H9"/>`,

  sun:
    `<circle cx="12" cy="12" r="4"/>` +
    `<path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2` +
    `M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>`,

  moon: `<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>`,

  // "Follow the system" theme option.
  monitor:
    `<rect x="2" y="3" width="20" height="14" rx="2"/>` +
    `<path d="M8 21h8"/>` +
    `<path d="M12 17v4"/>`,

  // The two soundstrip renderings, on the You screen. Bars on their own axis
  // for the waveform; marks at different heights inside a frame for the
  // spectrogram, which is what one looks like from across a room.
  waveform:
    `<path d="M3 11v2"/>` +
    `<path d="M7 7v10"/>` +
    `<path d="M11 4v16"/>` +
    `<path d="M15 8v8"/>` +
    `<path d="M19 10v4"/>`,

  spectrogram:
    `<rect x="2.5" y="3.5" width="19" height="17" rx="2"/>` +
    `<path d="M6 16h3"/>` +
    `<path d="M9 12h3"/>` +
    `<path d="M13 8h2"/>` +
    `<path d="M16 13h2"/>`,

  alert:
    `<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 ` +
    `1.73-3"/>` +
    `<path d="M12 9v4"/>` +
    `<path d="M12 17h.01"/>`,

  // Set a bird aside — the no-entry sign, which reads as "not this one" rather
  // than as the cross, which everywhere else in the app means a wrong answer.
  'set-aside': `<circle cx="12" cy="12" r="9"/><path d="m5.6 5.6 12.8 12.8"/>`,

  // Birdle. A board of squares with one of them filled in — the two things the
  // mode is made of, a grid of locks and a picture made of pixels. A bird glyph
  // would have been wrong twice over: every mode here is about birds, so it names
  // nothing, and at 21px a bird is a blob.
  birdle:
    `<rect x="3.5" y="3.5" width="17" height="17" rx="2"/>` +
    `<path d="M3.5 9.5h17"/>` +
    `<path d="M3.5 15.5h17"/>` +
    `<path d="M9.5 3.5v17"/>` +
    `<path d="M15.5 3.5v17"/>` +
    `<path d="M12.5 12.5h.01"/>`,

  // Birdle's past days. Lucide's calendar: a page, its two rings and the rule
  // under the month.
  calendar:
    `<rect x="3" y="4" width="18" height="18" rx="2"/>` +
    `<path d="M16 2v4"/>` +
    `<path d="M8 2v4"/>` +
    `<path d="M3 10h18"/>`,

  // The calendar's month before and month after. Chevrons rather than the
  // arrows, which on that screen's header already mean "leave".
  'chevron-left': `<path d="m15 18-6-6 6-6"/>`,

  'chevron-right': `<path d="m9 18 6-6-6-6"/>`,

  // The round's options, on the card that otherwise starts it straight away.
  // Faders rather than a cog: a cog means settings, which live on the You
  // screen, and these are choices made about the next round and forgotten.
  sliders:
    `<path d="M4 7h7"/><path d="M15 7h5"/><circle cx="13" cy="7" r="2"/>` +
    `<path d="M4 17h3"/><path d="M11 17h9"/><circle cx="9" cy="17" r="2"/>`,

  // Take the last answer back. The arrowhead sits on the LEFT end of the arc —
  // the direction of travel is backwards, and an arc alone reads as "refresh".
  undo:
    `<path d="M3 8h11a6 6 0 0 1 0 12h-6"/>` +
    `<path d="m7 4-4 4 4 4"/>`,

  // What the traits mean — Lucide's info circle, the one glyph everyone already
  // reads as "explain this".
  info: `<circle cx="12" cy="12" r="9"/><path d="M12 16v-4"/><path d="M12 8h.01"/>`,
};

/** Lucide's own line weight. Round caps turn a zero-length path into a dot. */
const STROKE = 2;

export interface IconOpts {
  /** Rendered pixel size. Default 20. */
  size?: number;
  /** When set, the icon is announced instead of hidden — for icons carrying meaning alone. */
  label?: string;
  /** Extra class on the wrapper. */
  class?: string;
}

/**
 * A `<span class="icon">` wrapping the SVG.
 *
 * The wrapper is not decoration: el() builds nodes with document.createElement,
 * which is HTML-namespaced and cannot produce SVG elements. Setting innerHTML on
 * an HTML parent does parse SVG correctly (the parser handles foreign content) —
 * the same trick donut-ring.ts already uses.
 */
export function icon(name: IconName, opts: IconOpts = {}): HTMLElement {
  const s = opts.size ?? 20;
  return el(`span.icon${opts.class ? '.' + opts.class : ''}`, {
    'aria-hidden': opts.label ? null : 'true',
    role: opts.label ? 'img' : null,
    'aria-label': opts.label ?? null,
    html:
      `<svg viewBox="0 0 24 24" width="${s}" height="${s}" fill="none" ` +
      `stroke="currentColor" stroke-width="${STROKE}" stroke-linecap="round" ` +
      `stroke-linejoin="round">${PATHS[name]}</svg>`,
  });
}

/**
 * Two icons also have to appear from CSS `content:` positions — the ✓/✗ result
 * marks on quiz choices. An SVG can't go in `content:`, and an SVG loaded as an
 * image document (via `content: url()` or `background-image`) ignores
 * currentColor, which would mean a separate data URI per state per theme.
 *
 * A mask over `background: currentColor` keeps the colour inheritance and needs
 * no JS at the grading call sites. These custom properties are generated from
 * the same PATHS above so the geometry is never written twice.
 */
const MASKED: IconName[] = ['check', 'cross'];

/**
 * A second, heavier weight of each mark. The quiz result mark is the one glyph
 * that has to read at a glance across a whole list of choices, and the stroke
 * that suits an inline icon goes wispy once the mark is scaled up.
 */
const MASK_WEIGHTS: { suffix: string; stroke: number }[] = [
  { suffix: '', stroke: STROKE },
  { suffix: '-bold', stroke: 3.4 },
];

function dataUri(inner: string, stroke: number): string {
  // stroke is explicit: mask-mode defaults to match-source, which reads alpha,
  // and currentColor has no inheritance context inside an image document.
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' ` +
    `stroke='#000' stroke-width='${stroke}' stroke-linecap='round' ` +
    `stroke-linejoin='round'>${inner}</svg>`;
  const escaped = svg.replace(
    /[<>#%{}"]/g,
    (c) => '%' + c.charCodeAt(0).toString(16),
  );
  return `url("data:image/svg+xml,${escaped}")`;
}

ensureStyle(
  'icon-masks',
  ':root{' +
    MASKED.flatMap((n) =>
      MASK_WEIGHTS.map(
        (w) => `--ico-${n}${w.suffix}:${dataUri(PATHS[n], w.stroke)};`,
      ),
    ).join('') +
    '}' +
    // Shared geometry for every masked mark.
    `.ico-mark{background:currentColor;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;` +
    `-webkit-mask-position:center;mask-position:center;` +
    `-webkit-mask-size:contain;mask-size:contain;}`,
);
